import React, { useState, useEffect, useMemo, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar, Area, AreaChart,ResponsiveContainer, PieChart, Pie, Cell, ReferenceLine } from 'recharts';
import RobotReportButton from './components/RobotReportButton';
import { 
  Activity,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Filter,
  Search,
  ChevronDown,
  ChevronRight,
  Home,
  GitBranch,
  Play,
  TestTube,
  TestTubes,
  Calendar,
  TrendingUp,
  AlertTriangle,
  ExternalLink,
  Loader2
} from 'lucide-react';
import azureDevOpsService from '../src/services/azureDevOpsService';
import TestResultsTable from './components/TestResultsTable';
import TestCaseSearchBar from './components/TestCaseSearchBar';
import FlakyRobotFrameworkTests from './FlakyRobotFrameworkTests';
import { toast, Toaster } from 'react-hot-toast';
import { getStatusFromSuccessRate } from './config/statusConfig';
import { computeTestStats, computeSuccessRate, matchesStatusFilter, statusLabel, statusChartColor } from './utils/testStatus';
import AiAnalysisDrawer from './components/AiAnalysisDrawer';
import rootCauseService from '../src/services/rootCauseService';
import bugLinkService from '../src/services/bugLinkService';
import BatchRootCauseBar from './components/BatchRootCauseBar';

// Router with URL sync (pushState + popstate) and robust parsing
const useRouter = () => {
  const [currentPath, setCurrentPath] = useState('/'); // full path (with base)
  const [uiPath, setUiPath] = useState('/'); // path without base prefix for internal routing
  const [params, setParams] = useState({});
  const baseRef = useRef(''); // e.g. '/robotframework' if mounted under that prefix

  const safeDecode = (val) => {
    try { return decodeURIComponent(val); } catch { return val; }
  };

  const parse = (path) => {
    const clean = path.split('?')[0] || '/';
    const parts = clean.split('/').filter(Boolean);
    const p = {};

    if (parts[0] === 'pipeline' && parts[1]) {
      p.pipelineId = parts[1];
      if (parts[2] === 'build' && parts[3]) {
        p.buildId = parts[3];
        if (parts[4] === 'run' && parts[5]) {
          p.runId = parts[5];
          if (parts[6] === 'testsuite' && parts[7]) {
            p.testSuiteName = safeDecode(parts.slice(7).join('/'));
          }
        }
      }
    } else if (parts[0] === 'testcase' && parts[1]) {
      p.testcaseName = safeDecode(parts[1]);
      if (parts[2] === 'history' && parts[3]) {
        p.pipelineId = parts[3];
        if (parts[4] === 'back') {
          // /testcase/:name/history/:pipelineId/back/:bp/build/:bb/run/:rr/testsuite/:ts(...)
          p.backPipelineId = parts[5];
          if (parts[6] === 'build' && parts[7]) p.backBuildId = parts[7];
          if (parts[8] === 'run' && parts[9]) p.backRunId = parts[9];
          if (parts[10] === 'testsuite' && parts[11]) {
            p.backTestSuiteName = safeDecode(parts.slice(11).join('/'));
          }
        }
      }
    }
    return p;
  };

  const stripBase = (path) => {
    const base = baseRef.current;
    if (base && path.startsWith(base)) {
      const stripped = path.slice(base.length) || '/';
      return stripped.startsWith('/') ? stripped : `/${stripped}`;
    }
    return path || '/';
  };

  const applyPath = (path) => {
    const effective = stripBase(path);
    setCurrentPath(path);
    setUiPath(effective);
    setParams(parse(effective));
  };

  const navigate = (path) => {
    if (path && path.startsWith('/tests')) {
      try {
        window.location.assign(path);
      } catch {
        window.location.href = path;
      }
      return;
    }
    try {
      const base = baseRef.current || '';
      const full = `${base}${path}`;
      window.history.pushState({}, '', full);
    } catch {}
    applyPath(path);
  };

  useEffect(() => {
    const initialFull = (typeof window !== 'undefined' && window.location && (window.location.pathname + window.location.search)) || '/';
    // Detect base prefix once on mount
    try {
      const pathname = (typeof window !== 'undefined' && window.location && window.location.pathname) || '/';
      baseRef.current = pathname.startsWith('/robotframework') ? '/robotframework' : '';
    } catch { baseRef.current = ''; }
    applyPath(initialFull);
    const onPop = () => {
      const pFull = window.location.pathname + window.location.search;
      applyPath(pFull);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  return { currentPath, uiPath, params, navigate, basePrefix: baseRef.current };
};

// Components
const StatusBadge = ({ status }) => {
  const styles = {
    succeeded: 'bg-green-100 text-green-800 border-green-300',
    success: 'bg-green-100 text-green-800 border-green-300',
    failed: 'bg-red-100 text-red-800 border-red-300',
    running: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    partiallysucceeded: 'bg-orange-100 text-orange-800 border-orange-300',
    passed: 'bg-green-100 text-green-800 border-green-300'
  };

  const icons = {
    success: <CheckCircle className="w-4 h-4" />,
    failed: <XCircle className="w-4 h-4" />,
    running: <Clock className="w-4 h-4" />,
    passed: <CheckCircle className="w-4 h-4" />
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.failed}`}>
      {icons[status]}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const SuccessRateBadge = ({ successRate }) => {
  const normalizedRate = Number(String(successRate ?? '').replace('%', '')) || 0;
  const status = getStatusFromSuccessRate(normalizedRate, 0);
  const styles = {
    green: 'bg-green-100 text-green-800 border-green-300',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    red: 'bg-red-100 text-red-800 border-red-300'
  };
  const icons = {
    green: <CheckCircle className="w-4 h-4" />,
    yellow: <AlertTriangle className="w-4 h-4" />,
    red: <XCircle className="w-4 h-4" />
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status.color]}`}>
      {icons[status.color]}
      {status.label}
    </span>
  );
};

const formatDuration = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = (seconds % 60).toFixed(0);
  return `${minutes}m ${remainingSeconds}s`;
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('fr-FR');
};

const getRobotReturnTo = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('returnTo') || localStorage.getItem('robotReturnTo');
  } catch {
    return null;
  }
};

// Navigation Component
const Navigation = ({ currentPath, navigate }) => {
  const pathOnly = (currentPath || '/').split('?')[0];
  const pathParts = pathOnly.split('/').filter(Boolean);
  const storedReturnTo = getRobotReturnTo();
  const dashboardPath = storedReturnTo || '/';
  
  const breadcrumbs = [
    { label: 'Dashboard', path: dashboardPath, icon: <Home className="w-4 h-4" /> }
  ];

  if (pathParts[0] === 'pipeline' && pathParts[1]) {
    breadcrumbs.push({
      label: pathParts[1],
      path: `/pipeline/${pathParts[1]}`,
      icon: <GitBranch className="w-4 h-4" />
    });

    if (pathParts[2] === 'build' && pathParts[3]) {
      breadcrumbs.push({
        label: `${pathParts[3]}`,
        path: `/pipeline/${pathParts[1]}/build/${pathParts[3]}`,
        icon: <Play className="w-4 h-4" />
      });

      if (pathParts[4] === 'run' && pathParts[5]) {
        breadcrumbs.push({
          label: `${pathParts[5]}`,
          path: `/pipeline/${pathParts[1]}/build/${pathParts[3]}/run/${pathParts[5]}`,
          icon: <TestTubes className="w-4 h-4" />
        });

        if (pathParts[6] === 'testsuite' && pathParts[7]) {
          const decodedTs = (() => { try { return decodeURIComponent(pathParts.slice(7).join('/')); } catch { return pathParts.slice(7).join('/'); } })();
          breadcrumbs.push({
            label: `${decodedTs}`,
            path: `/pipeline/${pathParts[1]}/build/${pathParts[3]}/run/${pathParts[5]}/testsuite/${pathParts.slice(7).join('/')}`,
            icon: <TestTube  className="w-4 h-4" />
          });
        }
      }
    }
  } else if (pathParts[0] === 'testcase' && pathParts[1]) {
    const decodedName = (() => { try { return decodeURIComponent(pathParts[1]); } catch { return pathParts[1]; } })();
    breadcrumbs.push({
      label: `${decodedName}`,
      path: currentPath,
      icon: <TestTube className="w-4 h-4" />
    });
  }

  return (
    <nav className="bg-white shadow-sm border-b mb-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 py-3">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.path}>
              {index > 0 && <ChevronRight className="w-4 h-4 text-gray-400" />}
              <button
                onClick={() => navigate(crumb.path)}
                className="flex items-center space-x-1 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors"
              >
                {crumb.icon}
                <span>{crumb.label}</span>
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>
    </nav>
  );
};

const pipelinesList = [
  { id: 601, name: "RF Admin API" },
  { id: 602, name: "RF Admin Browser" },
  { id: 603, name: "RF Config API" },
  { id: 604, name: "RF Config Browser" },
  { id: 605, name: "RF DE API" },
  { id: 606, name: "RF DE Browser" },
  { id: 607, name: "RF Pelias" },
  { id: 608, name: "RF DP API" },
  { id: 609, name: "RF DP Browser" },
  { id: 610, name: "RF Investigation API" },
  { id: 611, name: "RF Agentique API" },
  { id: 612, name: "RF Agentique Browser" },
  { id: 613, name: "RF Assistant API" },
  { id: 614, name: "RF Assistant Browser" },
  { id: 615, name: "RF OSINT API" },
  { id: 616, name: "RF OSINT Browser" },
];
const pipelineIds = [601, 602, 603, 604, 605, 606, 607, 608, 609, 610, 611, 612, 613, 614, 615, 616];


// Dashboard Component
const Dashboard = ({ navigate, pendingFilter }) => {
  const [pipelines, setPipelines] = useState([]);
  const [globalStats, setGlobalStats] = useState({});
  const [trendData, setTrendData] = useState({});
  const [trendDays, setTrendDays] = useState(7);
  const [trendLoading, setTrendLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [triggerLoading, setTriggerLoading] = useState(new Set());

  const handleTriggerPipeline = async (pipelineId) => {
    setTriggerLoading(prev => new Set([...prev, pipelineId]));
    try {
      await azureDevOpsService.triggerPipeline(pipelineId);
    } catch (err) {
      console.error('Failed to trigger pipeline:', err);
    } finally {
      setTriggerLoading(prev => { const s = new Set(prev); s.delete(pipelineId); return s; });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try{
      const pipelinesData = await azureDevOpsService.getBuildsData(pipelinesList);
      setPipelines(pipelinesData);
      setLoading(true);
      setError(null);

      // KPIs globaux
      let totalPassed = 0, totalFailed = 0, totalSkipped = 0, totalDuration = 0, totalTests = 0;

      for (const p of pipelinesData) {
        totalPassed += p.passedTests;
        totalFailed += p.failedTests;
        totalSkipped += p.skippedTests || 0;
        totalDuration += p.totalDuration;
        totalTests += p.totalTests;
      }

      setGlobalStats({
        totalPassed,
        totalFailed,
        totalSkipped,
        averageSuccessRate: computeSuccessRate(totalPassed, totalTests, totalSkipped),
        averageDuration: pipelinesData.length ? totalDuration / pipelinesData.length : 0
      });
    } catch (err) {
        console.error('Erreur lors du chargement des données:', err);
        setError('Impossible de charger les données des pipelines');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);  

  useEffect(() => {
    const fetchTrendData = async () => {
      setTrendLoading(true);
      try {
        const results = {};
        await Promise.all(pipelinesList.map(async (pipeline) => {
          try {
            results[pipeline.id] = await azureDevOpsService.getTrendDataForPipeline(pipeline.id, trendDays);
          } catch {
            results[pipeline.id] = [];
          }
        }));
        setTrendData(results);
      } catch (error) {
        console.error("Erreur chargement tendance tests:", error);
      } finally {
        setTrendLoading(false);
      }
    };
    fetchTrendData();
  }, [trendDays]);

  if (loading) {
    return <div className="text-center py-8">Chargement des données...</div>;
  }

  if (error) {
    return <div className="text-center py-8 text-red-600">{error}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Bannière filtre de recherche actif */}
      {pendingFilter && (
        <div className="flex items-center gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          <Search className="w-4 h-4 shrink-0" />
          <span>Filtre actif : <strong>{pendingFilter}</strong> — Naviguez dans un pipeline puis une suite pour voir les résultats filtrés.</span>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Tests Passés</p>
              <p className="text-2xl font-bold text-green-600">{globalStats.totalPassed}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Tests Échoués</p>
              <p className="text-2xl font-bold text-red-600">{globalStats.totalFailed}</p>
            </div>
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Tests Ignorés</p>
              <p className="text-2xl font-bold text-gray-500">{globalStats.totalSkipped || 0}</p>
            </div>
            <Clock className="w-8 h-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Taux de Réussite</p>
              <p className="text-2xl font-bold text-blue-600">{typeof globalStats.averageSuccessRate === 'number'? globalStats.averageSuccessRate.toFixed(1): '0.0'}%</p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Durée Moyenne</p>
              <p className="text-2xl font-bold text-purple-600">{formatDuration(globalStats.averageDuration)|| 0}</p>
            </div>
            <Clock className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Pipelines Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {pipelines.map((pipeline) => (
          <div key={pipeline.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{pipeline.name}</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleTriggerPipeline(pipeline.id)}
                  disabled={triggerLoading.has(pipeline.id)}
                  className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 border border-gray-200 disabled:opacity-50 transition-colors"
                  title="Lancer le pipeline"
                >
                  {triggerLoading.has(pipeline.id) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                </button>
                <SuccessRateBadge successRate={pipeline.successRate} />
              </div>
            </div>
            
            <div className="space-y-3">
              {pipeline.executionDate && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Date d'exécution:</span>
                  <span className="font-medium">{formatDate(pipeline.executionDate)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Dernier Build:</span>
                <span className="font-medium">{pipeline.lastBuildId}</span>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tests:</span>
                <span className="font-medium">
                  <span className="text-green-600">{pipeline.passedTests}</span> / 
                  <span className="text-red-600">{pipeline.failedTests}</span> / 
                  {pipeline.skippedTests > 0 && <><span className="text-gray-500">{pipeline.skippedTests}</span> / </>}
                  <span className="text-gray-900">{pipeline.totalTests}</span>
                </span>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Durée:</span>
                <span className="font-medium">{formatDuration(pipeline.totalDuration)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Taux de réussite:</span>
                <span className="font-medium">{pipeline.successRate}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full" 
                  style={{ width: `${pipeline.successRate || 0}%` }}
                ></div>
              </div>
              
              <button
                onClick={() => navigate(`/pipeline/${pipeline.id}`)}
                className="w-full mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Voir Détails
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Trends Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Évolution des Tests</h3>
          <div className="flex items-center gap-1">
            {[7, 14, 21].map(d => (
              <button key={d} onClick={() => setTrendDays(d)}
                className={`px-2 py-0.5 text-xs rounded transition-colors ${trendDays === d ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                {d}j
              </button>
            ))}
          </div>
        </div>
        {trendLoading ? (
          <div className="h-40 flex items-center justify-center text-sm text-gray-400">Chargement...</div>
        ) : Object.values(trendData).some(arr => arr.length > 0) ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pipelinesList.map(pipeline => {
              const data = trendData[pipeline.id] || [];
              const lastRun = data[data.length - 1];
              const successRate = lastRun && lastRun.total > 0 ? Math.round((lastRun.success / lastRun.total) * 100) : null;
              const maxTotal = data.length > 0 ? Math.max(...data.map(d => d.total)) : 0;
              return (
                <div key={pipeline.id} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-800">{pipeline.name}</span>
                    {successRate !== null && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${successRate >= 90 ? 'bg-green-100 text-green-700' : successRate >= 70 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                        {successRate}%
                      </span>
                    )}
                  </div>
                  {data.length > 0 ? (
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={data} margin={{ top: 4, right: 8, bottom: 40, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="sessionLabel" tick={{ fontSize: 9 }} interval={0} angle={-30} textAnchor="end" />
                        <YAxis tick={{ fontSize: 9 }} width={28} />
                        <Tooltip content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0].payload;
                          return (
                            <div className="bg-white border border-gray-200 rounded shadow p-2 text-xs">
                              <p className="font-semibold">{d.sessionLabel}</p>
                              {d.buildNumber && <p className="text-gray-400">Build {d.buildNumber}</p>}
                              <p className="text-green-600">✓ {d.success}</p>
                              <p className="text-red-500">✗ {d.failed}</p>
                              <p className="text-gray-600">= {d.total}</p>
                            </div>
                          );
                        }} />
                        {maxTotal > 0 && <ReferenceLine y={maxTotal} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: `${maxTotal}`, position: 'insideTopRight', fontSize: 9, fill: '#f59e0b' }} />}
                        <Bar dataKey="success" stackId="a" fill="#10b981" />
                        <Bar dataKey="failed" stackId="a" fill="#ef4444" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-40 flex items-center justify-center text-xs text-gray-400">Aucune exécution</div>
                  )}
                  {lastRun && <p className="text-xs text-gray-400 mt-1">Dernier run : {lastRun.sessionLabel}</p>}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="h-40 flex items-center justify-center text-sm text-gray-500">
            Aucune exécution sur la période sélectionnée.
          </div>
        )}
      </div>
    </div>
  );
};
// Pipeline Details Component
const PipelineDetails = ({ pipelineId, navigate }) => {
  const [pipelines, setPipelines] = useState([]);
  const [pBuilds, setBuilds] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const pipelinesData = await azureDevOpsService.getBuildsData(pipelinesList);
      setPipelines(pipelinesData);      
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchBuildData = async () => {
      try {
        const buildsData = await azureDevOpsService.getRecentBuildsByPipeline(pipelineId);

        setBuilds(buildsData);
        
        // Redirection automatique vers le dernier build (le plus récent)
        const builds = buildsData[`pipeline-${String(pipelineId)}`] || [];
        if (builds.length > 0) {
          const latestBuild = builds[0]; // Le premier build est normalement le plus récent
          navigate(`/pipeline/${pipelineId}/build/${latestBuild.buildId}`);
          return; // Sortir de la fonction pour éviter de continuer le rendu
        }
      } catch (error) {
        console.error("Erreur chargement des builds:", error);
      }
    };

    fetchBuildData();
  }, [pipelineId, navigate]);

  // Message d'attente pendant la redirection
  return (
    <div className="flex justify-center items-center h-64">
      {/* <div className="text-gray-600">Redirection vers le dernier build...</div> */}
    </div>
  );
};

// Build Details Component
const BuildDetails = ({ pipelineId, buildId, navigate }) => {
  const [pipelines, setPipelines] = useState([]);
  const [testSuites, setTestSuites] = useState([]);
  const [pBuilds, setBuilds] = useState([]);
  const [activeTab, setActiveTab] = useState('suites'); // Par défaut sur les test suites
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {      
        const [testSuiteData, pipelinesData, buildsData, trendBuilds] = await Promise.all([
          azureDevOpsService.getTestSuitesForBuild(buildId),
          azureDevOpsService.getBuildsData(pipelinesList),
          azureDevOpsService.getRecentBuildsByPipeline(pipelineId),
          azureDevOpsService.getTrendDataFromBuilds(pipelineId)
        ]);

        const suitesArray = testSuiteData[`build-${buildId.replace('build-', '')}`] || [];
        setTestSuites(suitesArray);
        setPipelines(pipelinesData);
        setBuilds(buildsData);
        setTrendData(trendBuilds);
        setError(null);
        setLoading(false);
      } catch (error) {
        console.error('Erreur chargement des données:', error);
        setError('Impossible de charger les données');
        setLoading(false);
      }
    };
    fetchAll();
  }, [buildId, pipelineId]);

  const pipeline = pipelines.find((p) => String(p.id) === String(pipelineId));
  const pipelineName = (pipeline && pipeline.name) || (pipelinesList.find(p => String(p.id) === String(pipelineId))?.name) || `Pipeline ${pipelineId}`;
  const builds = pBuilds[`pipeline-${String(pipelineId)}`] || [];

  if (loading) {
    return <div className="text-center py-8">Chargement des résultats du build...</div>;
  }
  if (error) {
    return <div className="text-center py-8 text-red-600">{error}</div>;
  }
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{buildId}</h2>
            <p className="text-gray-600">{pipelineName}</p>
          </div>
          <button
            onClick={() => {
              const returnTo = getRobotReturnTo();
              if (returnTo) {
                window.location.href = returnTo;
              } else {
                navigate(`/`);
              }
            }}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors"
          >
            ← Retour Pipelines
          </button>
        </div>
        
        {/* Statistiques du build actuel */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Test Suites</span>
              <span className="text-lg font-bold text-blue-600">{testSuites.length}</span>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Tests Passés</span>
              <span className="text-lg font-bold text-green-600">
                {testSuites.reduce((sum, suite) => sum + suite.passed, 0)}
              </span>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Tests Échoués</span>
              <span className="text-lg font-bold text-red-600">
                {testSuites.reduce((sum, suite) => sum + suite.failed, 0)}
              </span>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Tests Ignorés</span>
              <span className="text-lg font-bold text-gray-500">
                {testSuites.reduce((sum, suite) => sum + (suite.skipped || 0), 0)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('suites')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'suites'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Test Suites
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Historique Builds
            </button>
            <button
              onClick={() => setActiveTab('table')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'table'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Vue Tableau
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Onglet Test Suites - Vue par défaut */}
          {activeTab === 'suites' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {testSuites.map((suite) => {
                const successRate = computeSuccessRate(suite.passed, suite.totalTests, suite.skipped);
                return (
                <div key={suite.runId} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">{suite.name}</h4>
                    <SuccessRateBadge successRate={successRate} />
                  </div>
                  
                  <div className="flex justify-between text-sm mb-3 pb-3 border-b border-gray-200">
                    <span className="text-gray-600">Environnement de test:</span>
                    <span className="font-medium">{suite.testEnvironment || 'Non défini'}</span>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total:</span>
                      <span className="font-medium">{suite.totalTests} tests</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Passés:</span>
                      <span className="font-medium text-green-600">{suite.passed}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Échoués:</span>
                      <span className="font-medium text-red-600">{suite.failed}</span>
                    </div>
                    {suite.skipped > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Ignorés:</span>
                        <span className="font-medium text-gray-500">{suite.skipped}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Durée:</span>
                      <span className="font-medium">{formatDuration(suite.duration)}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate(`/pipeline/${pipelineId}/build/${buildId}/run/${suite.runId}/testsuite/${encodeURIComponent(suite.name)}`)}
                    className="w-full mt-3 bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 transition-colors"
                  >
                    Voir Détails
                  </button>
                </div>
              );
              })}
            </div>
          )}

          {/* Onglet Historique des Builds */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Historique des 5 derniers builds</h3>
              <div className="divide-y divide-gray-200">
                {builds.map((build) => (
                  <div key={build.buildId} className={`p-6 hover:bg-gray-50 transition-colors ${
                    build.buildId === buildId ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <StatusBadge status={build.status} />
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {build.buildId}
                            {build.buildId === buildId && (
                              <span className="ml-2 text-sm text-blue-600 font-normal">(Actuel)</span>
                            )}
                          </h4>
                          <p className="text-sm text-gray-600">{formatDate(build.date)}</p>
                        </div>
                      </div>

                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Tests:</span>
                        <span className="font-medium ml-2">
                          <span className="text-green-600">{build.passedTests}</span> / 
                          <span className="text-red-600">{build.failedTests}</span> / 
                          {build.skippedTests > 0 && <><span className="text-gray-500">{build.skippedTests}</span> / </>}
                          <span className="text-gray-900">{build.totalTests}</span>
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-600">{formatDuration(build.duration)}</span>
                        <RobotReportButton buildId={build.buildId} kind="report" />
                        <RobotReportButton buildId={build.buildId} kind="log" />
                        {build.buildId !== buildId && (
                          <button
                            onClick={() => navigate(`/pipeline/${pipelineId}/build/${build.buildId}`)}
                            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                          >
                            Voir ce Build
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Status Evolution Chart */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution des Statuts</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="success" stroke="#10b981" strokeWidth={2} name="Succès" />
                    <Line type="monotone" dataKey="failed" stroke="#ef4444" strokeWidth={2} name="Échecs" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Onglet Vue Tableau */}
          {activeTab === 'table' && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Suite</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nb Tests</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Passés</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Échoués</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ignorés</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durée</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {testSuites.map((suite) => (
                    <tr key={suite.runId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{suite.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">{suite.totalTests}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-green-600 font-medium">{suite.passed}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-red-600 font-medium">{suite.failed}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500 font-medium">{suite.skipped || 0}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">{formatDuration(suite.duration)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/pipeline/${pipelineId}/build/${buildId}/run/${suite.runId}/testsuite/${encodeURIComponent(suite.name)}`)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                        >
                          Détails
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Test Suite Details Component
const TestSuiteDetails = ({ pipelineId, buildId, runId, testSuiteName, navigate, initialSearchTerm = '' }) => {
  const [testCases, setTestCases] = useState([]);
  const [relatedBugsMap, setRelatedBugsMap] = useState({});
  const [showBugsModal, setShowBugsModal] = useState(false);
  const [modalBugs, setModalBugs] = useState([]);
  const [modalBugTestName, setModalBugTestName] = useState(null);
  const [showAiAnalysis, setShowAiAnalysis] = useState(false);
  const [selectedAiTestCase, setSelectedAiTestCase] = useState(null);
  // Map des tests marqués Non reproduit (persistant)
  const nonReproStorageKey = useMemo(() => `robotframework:nonrepro:${pipelineId}:${runId}:${encodeURIComponent(testSuiteName)}`,[pipelineId, runId, testSuiteName]);
  
  // Initialiser depuis localStorage directement (lazy initialization)
  const [nonReproMap, setNonReproMap] = useState(() => {
    try {
      const raw = localStorage.getItem(`robotframework:nonrepro:${pipelineId}:${runId}:${encodeURIComponent(testSuiteName)}`);
      if (raw) {
        const data = JSON.parse(raw);
        const today = new Date().toDateString();
        if (data.date === today && data.values && typeof data.values === 'object') {
          return data.values;
        }
      }
    } catch(e) {
      console.warn('⚠️ Charge nonReproMap échoué', e);
    }
    return {};
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  // Persister à chaque changement (sauf initialisation)
  useEffect(() => {
    try {
      const today = new Date().toDateString();
      const data = { date: today, values: nonReproMap };
      localStorage.setItem(nonReproStorageKey, JSON.stringify(data));
    } catch(e) {
      console.warn('⚠️ Stockage nonReproMap échoué', e);
    }
  }, [nonReproMap, nonReproStorageKey]);

  const [nonReproLoading, setNonReproLoading] = useState(new Set());

  const markNonRepro = async (testName, testResultId) => {
    if (nonReproMap[testName] || nonReproLoading.has(testName)) return;
    setNonReproLoading(prev => new Set([...prev, testName]));
    try {
      await azureDevOpsService.updateTestResultOutcome(runId, testResultId);
      setNonReproMap(prev => ({ ...prev, [testName]: true }));
      setTestCases(prev => prev.map(t => t.name === testName ? { ...t, status: 'passed' } : t));
    } catch (err) {
      console.error('Erreur mise à jour AzDO non-reproduit:', err);
      setNonReproMap(prev => ({ ...prev, [testName]: true }));
      setTestCases(prev => prev.map(t => t.name === testName ? { ...t, status: 'passed' } : t));
    } finally {
      setNonReproLoading(prev => { const n = new Set(prev); n.delete(testName); return n; });
    }
  };

  const [rootCauseMap, setRootCauseMap] = useState({});
  const [selectedTests, setSelectedTests] = useState(new Set());
  const [notesMap, setNotesMap] = useState({});
  useEffect(() => {
    if (!buildId) return;
    rootCauseService.getForBuild(pipelineId, buildId)
      .then(({ rootCauses, notes }) => { setRootCauseMap(rootCauses); setNotesMap(notes); })
      .catch(err => console.warn('Failed to load root causes:', err));
  }, [pipelineId, buildId]);
  const handleRootCause = async (testName, cause) => {
    const prev = rootCauseMap[testName];
    setRootCauseMap(m => ({ ...m, [testName]: cause }));
    try {
      if (cause) {
        await rootCauseService.set({ pipelineId, buildId, runId, testName, rootCause: cause, squad: testSuiteName });
      } else {
        await rootCauseService.remove({ pipelineId, buildId, testName });
      }
    } catch (err) {
      console.error('Failed to save root cause:', err);
      setRootCauseMap(m => ({ ...m, [testName]: prev }));
    }
  };
  const handleBatchRootCause = async (cause) => {
    const names = [...selectedTests];
    const prev = { ...rootCauseMap };
    setRootCauseMap(m => {
      const next = { ...m };
      if (cause) names.forEach(nm => { next[nm] = cause; });
      else names.forEach(nm => { delete next[nm]; });
      return next;
    });
    try {
      await Promise.all(names.map(nm =>
        cause
          ? rootCauseService.set({ pipelineId, buildId, runId, testName: nm, rootCause: cause, squad: testSuiteName })
          : rootCauseService.remove({ pipelineId, buildId, testName: nm })
      ));
    } catch (err) {
      console.error('Failed to save batch root cause:', err);
      setRootCauseMap(prev);
    }
    setSelectedTests(new Set());
  };

  const handleNoteSave = async (testName, notes) => {
    try {
      await rootCauseService.setNotes({ pipelineId, buildId, testName, notes });
    } catch (err) {
      console.error('Failed to save note:', err);
    }
  };

  const [linkBugStatus, setLinkBugStatus] = useState(null);

  const linkOneBug = async (testName, bugId) => {
    const summary = await azureDevOpsService.getWorkItemSummary(bugId);
    if ((summary.type || '').toLowerCase() !== 'bug') {
      throw new Error(`#${summary.id} n'est pas un Bug (${summary.type || 'type inconnu'})`);
    }
    let linked = summary;
    try {
      await azureDevOpsService.linkBugToTestCaseByTitle(testName, summary.id);
    } catch (err) {
      if (!/Test Case introuvable/.test(err.message || '')) throw err;
      // Pas de Test Case ADO pour ce test: on garde le lien côté app uniquement
      await bugLinkService.add({ pipelineId, buildId, testName, bug: summary });
      linked = { ...summary, local: true };
    }
    setRelatedBugsMap(prev => {
      const existing = prev[testName] || [];
      if (existing.some(b => b.id === linked.id)) return prev;
      return { ...prev, [testName]: [...existing, linked] };
    });
  };

  const handleBatchLinkBug = async (bugId) => {
    const summary = await azureDevOpsService.getWorkItemSummary(bugId);
    if ((summary.type || '').toLowerCase() !== 'bug') {
      setLinkBugStatus({ type: 'error', message: `#${summary.id} n'est pas un Bug (${summary.type || 'type inconnu'})` });
      return;
    }

    const names = [...selectedTests].filter(
      nm => !(relatedBugsMap[nm] || []).some(b => b.id === summary.id)
    );

    if (names.length === 0) {
      setLinkBugStatus({ type: 'error', message: 'Tests déjà liés à ce bug' });
      return;
    }

    const linkedByTest = {};
    const failed = [];
    let localCount = 0;

    await Promise.all(names.map(async (nm) => {
      try {
        await azureDevOpsService.linkBugToTestCaseByTitle(nm, summary.id);
        linkedByTest[nm] = summary;
      } catch (err) {
        if (!/Test Case introuvable/.test(err.message || '')) {
          failed.push(nm);
          return;
        }
        try {
          await bugLinkService.add({ pipelineId, buildId, testName: nm, bug: summary });
          linkedByTest[nm] = { ...summary, local: true };
          localCount++;
        } catch {
          failed.push(nm);
        }
      }
    }));

    setRelatedBugsMap(prev => {
      const next = { ...prev };
      Object.entries(linkedByTest).forEach(([nm, bug]) => {
        const existing = next[nm] || [];
        next[nm] = existing.some(b => b.id === bug.id) ? existing : [...existing, bug];
      });
      return next;
    });

    const successCount = Object.keys(linkedByTest).length;
    setLinkBugStatus(
      failed.length > 0
        ? { type: 'error', message: `${successCount} test(s) lié(s), ${failed.length} échec(s)` }
        : { type: 'success', message: `${successCount} test(s) lié(s) au bug #${summary.id}${localCount ? ` (${localCount} en local, hors ADO)` : ''}` }
    );
    setSelectedTests(new Set());
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Robot Framework n'a pas besoin du paramètre version, on utilise runId tel quel
        const results = await azureDevOpsService.getTestCasesForRun(runId, 'robotframework');
        setTestCases(results);
      } catch (err) {
        console.error('❌ Error in getTestCasesForRun:', err);
        setError('Erreur lors de la récupération des tests.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [runId]);

  // Charger les bugs liés pour chaque test case (batched)
  useEffect(() => {
    let cancelled = false;
    if (!testCases || testCases.length === 0) {
      setRelatedBugsMap({});
      return;
    }

    const fetchRelatedBugs = async () => {
      const map = {};
      const concurrency = 5;
      for (let i = 0; i < testCases.length && !cancelled; i += concurrency) {
        const batch = testCases.slice(i, i + concurrency);
        try {
          const promises = batch.map(tc =>
            azureDevOpsService.getRelatedBugsForTestCaseByTitle(tc.name).catch(err => {
              console.warn('Erreur getRelatedBugs for', tc.name, err);
              return [];
            })
          );
          const results = await Promise.all(promises);
          batch.forEach((tc, idx) => {
            map[tc.name] = results[idx] || [];
          });
        } catch (err) {
          console.error('Erreur batch fetching related bugs:', err);
        }
      }

      try {
        const localMap = await bugLinkService.getForBuild(pipelineId, buildId);
        Object.entries(localMap).forEach(([testName, bugs]) => {
          const existing = map[testName] || [];
          const merged = [...existing];
          bugs.forEach(b => { if (!merged.some(m => m.id === b.id)) merged.push({ ...b, local: true }); });
          map[testName] = merged;
        });
      } catch (err) {
        console.warn('Erreur récupération bugs liés locaux:', err);
      }

      if (!cancelled) setRelatedBugsMap(map);
    };

    fetchRelatedBugs();
    return () => { cancelled = true; };
  }, [testCases, pipelineId, buildId]);

  const filteredTests = testCases
    .filter(test => {
      const matchesSearch = test.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = matchesStatusFilter(test.status, statusFilter);
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'duration':
          return (b.duration || 0) - (a.duration || 0);
        case 'status':
          return a.status.localeCompare(b.status);
        default:
          return a.name.localeCompare(b.name);
      }
    });

  // Statistiques pour la vue résumé
  const { total: totalTests, passed: passedTests, failed: failedTests, skipped: skippedTests, successRate } = computeTestStats(testCases);

  if (loading) {
    return <div className="flex justify-center items-center h-64">Chargement des tests...</div>;
  }

  if (error) {
    return <div className="text-red-600 text-center">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Suite de Tests: {testSuiteName}</h2>
            <p className="text-gray-600">Build: {buildId} | Run: {runId}</p>
          </div>
          <button
            onClick={() => navigate(`/pipeline/${pipelineId}/build/${buildId}`)}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors"
          >
            ← Retour Build
          </button>
        </div>

        {/* Statistiques de la test suite */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Total Tests</span>
              <span className="text-lg font-bold text-gray-900">{totalTests}</span>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Passés</span>
              <span className="text-lg font-bold text-green-600">{passedTests}</span>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Échoués</span>
              <span className="text-lg font-bold text-red-600">{failedTests}</span>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Ignorés</span>
              <span className="text-lg font-bold text-gray-500">{skippedTests}</span>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Taux Réussite</span>
              <span className="text-lg font-bold text-blue-600">{successRate}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Onglet Tests - Vue tableau */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <div className="px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900">Tests</h3>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Rechercher un test..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="passed">Passés</option>
                  <option value="failed">Échoués</option>
                  <option value="skipped">Ignorés</option>
                </select>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="name">Trier par nom</option>
                  <option value="status">Trier par statut</option>
                  <option value="duration">Trier par durée</option>
                </select>
              </div>
            </div>

            {/* Test Cases Table */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-900">
                  Tests Filtrés ({filteredTests.length})
                </h3>
              </div>

              <TestResultsTable
                tests={filteredTests}
                columns={{ rootCause: true }}
                nameLabel="Nom du Test"
                StatusBadge={StatusBadge}
                selectedTests={selectedTests}
                onSelectedTestsChange={setSelectedTests}
                relatedBugsMap={relatedBugsMap}
                nonReproMap={nonReproMap}
                nonReproLoading={nonReproLoading}
                onMarkNonRepro={markNonRepro}
                onLinkBug={linkOneBug}
                onShowBugs={(bugs, testName) => { setModalBugs(bugs); setModalBugTestName(testName); setShowBugsModal(true); }}
                rootCauseMap={rootCauseMap}
                notesMap={notesMap}
                onRootCause={handleRootCause}
                onNoteChange={(testName, val) => setNotesMap(m => ({ ...m, [testName]: val }))}
                onNoteBlur={handleNoteSave}
                renderName={test => (
                  <div className="flex items-center gap-2">
                    {test.isNewFailure && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-300">
                        New
                      </span>
                    )}
                    <div className="text-sm font-medium text-gray-900">{test.name}</div>
                  </div>
                )}
                renderActions={test => (
                  <>
                    {test.logUrl && (
                      <a
                        href={test.logUrl}
                        className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Log
                      </a>
                    )}
                    <button
                      onClick={() => navigate(`/testcase/${encodeURIComponent(test.name)}/history/${pipelineId}/back/${pipelineId}/build/${buildId}/run/${runId}/testsuite/${encodeURIComponent(testSuiteName)}`)}
                      className="text-indigo-600 hover:text-indigo-800"
                      title="Voir l'historique d'exécution"
                    >
                      Historique
                    </button>
                    {test.status === 'failed' && (
                      <button
                        onClick={() => { setSelectedAiTestCase(test); setShowAiAnalysis(true); }}
                        className="text-purple-600 hover:text-purple-800"
                        title="Analyser l'échec avec l'IA"
                      >
                        Analyser
                      </button>
                    )}
                  </>
                )}
                emptyState={(
                  <div className="text-center py-12">
                    <TestTube className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun test trouvé</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Essayez de modifier vos filtres de recherche.
                    </p>
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      </div>
      <BatchRootCauseBar
        count={selectedTests.size}
        onApply={handleBatchRootCause}
        onClear={() => { setSelectedTests(new Set()); setLinkBugStatus(null); }}
        onLinkBug={handleBatchLinkBug}
        linkBugStatus={linkBugStatus}
      />
      {/* Modal Bugs liés */}
      <AiAnalysisDrawer
        open={showAiAnalysis}
        onClose={() => {
          setShowAiAnalysis(false);
          setSelectedAiTestCase(null);
        }}
        testCase={selectedAiTestCase}
        context={{
          source: 'robotframework',
          pipelineId,
          buildId,
          runId,
          testSuiteName
        }}
      />
      <BugsModal
        show={showBugsModal}
        bugs={modalBugs}
        testName={modalBugTestName}
        onClose={() => setShowBugsModal(false)}
      />
    </div>
  );
};

// Modal Bugs liés (RobotFramework)
const BugsModal = ({ show, bugs, testName, onClose }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Bugs liés: {testName}</h3>
            <p className="text-sm text-gray-500">{(bugs || []).length} bug(s) trouvés</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle className="w-6 h-6" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          {(!bugs || bugs.length === 0) ? (
            <div className="text-gray-600 text-center py-8">Aucun bug lié trouvé.</div>
          ) : (
            bugs.map(b => (
              <div key={b.id} className="border rounded p-3 flex justify-between items-start hover:bg-gray-50 transition-colors">
                <div className="flex-1">
                  <a href={b.url} target="_blank" rel="noreferrer" className="text-gray-700 font-medium hover:underline">#{b.id} — {b.title}</a>
                  <div className="text-sm text-gray-600 mt-1">Etat: {b.state}</div>
                </div>
                <div className="text-right text-sm ml-4">
                  <a href={b.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">Ouvrir <ExternalLink className="w-4 h-4"/></a>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="flex items-center justify-end p-4 border-t bg-gray-50">
          <button onClick={onClose} className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">Fermer</button>
        </div>
      </div>
    </div>
  );
};

// Test Case History Component for Robot Framework (aligned with ReadyApi style + filters & back)
const RobotTestCaseHistory = ({ testcaseName, pipelineId, navigate, backPipelineId, backBuildId, backRunId, backTestSuiteName }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await azureDevOpsService.getAutomatedTestHistory(testcaseName, pipelineId);
        const correctedData = (data || []).map((item, index) => {
          const fullTimestamp = item.dateTimestamp || item.completedDate || item.startedDate || Date.now() - (index * 24 * 60 * 60 * 1000);
          const dateObj = new Date(fullTimestamp);
          return {
            ...item,
            dateTimestamp: fullTimestamp,
            date: item.date || dateObj.toISOString().split('T')[0],
            dateTime: dateObj.toISOString(),
            value: 1,
            statusDisplay: statusLabel(item.status)
          };
        })
        .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
        setHistoryData(correctedData);
      } catch (e) {
        console.error('Erreur chargement historique test:', e);
        setError("Impossible de charger l'historique");
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [testcaseName, pipelineId]);

  const filteredData = historyData.filter(item => {
    const matchStatus = matchesStatusFilter(item.status, statusFilter);
    const matchDate = !dateFilter || item.date === dateFilter;
    return matchStatus && matchDate;
  });

  const { total: totalTests, failed: failedTests, skipped: skippedTests, successRate } = computeTestStats(filteredData);
  const avgDuration = totalTests > 0 ? Math.round(filteredData.reduce((sum, d) => sum + (d.durationSeconds || d.duration || 0), 0) / totalTests) : 0;

  if (loading) return <div className="text-center py-8">Chargement de l'historique...</div>;
  if (error) return <div className="text-center py-8 text-red-600">{error}</div>;
  if (historyData.length === 0) return <div className="text-center py-8 text-gray-500">Aucun historique trouvé pour ce test</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Historique du Test</h2>
            <p className="text-gray-600">{testcaseName}</p>
          </div>
          <button 
            onClick={() => {
              if (backPipelineId && backBuildId && backRunId && backTestSuiteName) {
                const ts = encodeURIComponent(backTestSuiteName);
                navigate(`/pipeline/${backPipelineId}/build/${backBuildId}/run/${backRunId}/testsuite/${ts}`);
              } else {
                const returnTo = getRobotReturnTo();
                if (returnTo) {
                  window.location.href = returnTo;
                } else {
                  navigate(`/`);
                }
              }
            }} 
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors">
            ← Retour
          </button>
        </div>

        {/* Filtres */}
        <div className="mt-4 flex flex-wrap gap-4">
          <select
            className="border px-2 py-1 rounded"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Tous les statuts</option>
            <option value="passed">Passé</option>
            <option value="failed">Échoué</option>
            <option value="skipped">Ignoré</option>
          </select>

          <input
            type="date"
            className="border px-2 py-1 rounded"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />

          <button
            className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            onClick={() => { setStatusFilter('all'); setDateFilter(''); }}
          >
            Réinitialiser
          </button>
        </div>
      </div>

      {/* Statistiques en en-tête */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{totalTests}</div>
          <div className="text-sm text-gray-600">Exécutions</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{successRate}%</div>
          <div className="text-sm text-gray-600">Taux de réussite</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{failedTests}</div>
          <div className="text-sm text-gray-600">Échecs</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-gray-500">{skippedTests}</div>
          <div className="text-sm text-gray-600">Ignorés</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{avgDuration}ms</div>
          <div className="text-sm text-gray-600">Durée moyenne</div>
        </div>
      </div>

      {/* Graphique Statut */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibent text-gray-900 mb-4">Historique des Statuts d'Exécution</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={filteredData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={80} />
            <YAxis domain={[0, 1]} tick={false} label={{ value: 'Statut', angle: -90, position: 'insideLeft' }} />
            <Tooltip formatter={(value, name, props) => [props.payload.statusDisplay, 'Statut']} labelFormatter={(label) => `Date: ${label}`} />
            <Bar dataKey="value" name="Statut d'exécution">
              {filteredData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={statusChartColor(entry.status)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Évolution de la Durée */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution de la Durée</h3>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={filteredData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} label={{ value: 'Durée (s)', angle: -90, position: 'insideLeft' }} />
            <Tooltip formatter={(value) => [`${value}s`, 'Durée']} labelFormatter={(label) => `Date: ${label}`} />
            <Area type="monotone" dataKey="durationSeconds" stroke="#3b82f6" strokeWidth={2} fill="url(#colorDuration)" name="Durée d'exécution" />
            <defs>
              <linearGradient id="colorDuration" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Détails par Build */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Détails par Build</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Build</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durée</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.map((entry, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(entry.dateTime).toLocaleString('fr-FR', { 
                      year: 'numeric', 
                      month: '2-digit', 
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{entry.buildId}</td>
                  <td className="px-6 py-4 whitespace-nowrap"><StatusBadge status={entry.status} /></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.duration}s</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {entry.logUrl && (
                      <a href={entry.logUrl} className="text-blue-600 hover:text-blue-800 flex items-center gap-1" target="_blank" rel="noreferrer">
                        <ExternalLink className="w-3 h-3" />
                        Voir Log
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const RobotFrameworkPage = () => {
  const { uiPath, params, navigate } = useRouter();
  const [pendingFilter, setPendingFilter] = useState('');

  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const returnTo = urlParams.get('returnTo');
      if (returnTo) localStorage.setItem('robotReturnTo', returnTo);
      const q = urlParams.get('q');
      if (q) setPendingFilter(q);
    } catch {}
  }, []);

  const renderContent = () => {
    if (uiPath === '/') {
      return <Dashboard navigate={navigate} pendingFilter={pendingFilter} />;
    } else if (uiPath.startsWith('/testcase/') && params.testcaseName) {
      return (
        <RobotTestCaseHistory
          testcaseName={params.testcaseName}
          pipelineId={params.pipelineId}
          navigate={navigate}
          backPipelineId={params.backPipelineId}
          backBuildId={params.backBuildId}
          backRunId={params.backRunId}
          backTestSuiteName={params.backTestSuiteName}
        />
      );
    } else if (uiPath.startsWith('/pipeline/') && params.pipelineId) {
      if (params.runId && params.testSuiteName) {
        return (
          <TestSuiteDetails
            pipelineId={params.pipelineId}
            buildId={params.buildId}
            runId={params.runId}
            testSuiteName={params.testSuiteName}
            navigate={navigate}
            initialSearchTerm={pendingFilter}
          />
        );
      } else if (params.buildId) {
        return (
          <BuildDetails
            pipelineId={params.pipelineId}
            buildId={params.buildId}
            navigate={navigate}
          />
        );
      } else {
        return (
          <PipelineDetails
            pipelineId={params.pipelineId}
            navigate={navigate}
          />
        );
      }
    }

    return <div className="text-red-600">Page non trouvée</div>;
  };

  return (
    <div className="min-h-screen bg-gray-100">

      {/* Navigation */}
      <Navigation currentPath={uiPath} navigate={navigate} />

      {/* Barre de recherche globale */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <TestCaseSearchBar
            onSelect={async (result) => {
              setPendingFilter(result.title);
              try {
                const location = await azureDevOpsService.findTestCaseLocation(result.id, result.title, pipelineIds);
                if (location) {
                  navigate(`/pipeline/${location.pipelineId}/build/${location.buildId}/run/${location.runId}/testsuite/${encodeURIComponent(location.suiteName)}`);
                  return;
                }
              } catch (e) {
                console.warn('findTestCaseLocation échec:', e);
              }
              navigate('/');
            }}
            pendingFilter={pendingFilter}
            onClearFilter={() => setPendingFilter('')}
          />
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {renderContent()}
      </main>
    </div>
  );
};

export default RobotFrameworkPage;
